/**
 * The patient's documents, in the file workspace (QA report §5.2).
 *
 * The desk that files documents (ATTACHMENT_MANAGE) gets the drop zone; every
 * desk that reads the file gets the grid, with pictures showing without a
 * click. Categories are chips: the suggested set (روشتات، نتائج التحاليل، …)
 * plus every category this hospital has already used — the vocabulary still
 * grows by use.
 */

import { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog, ErrorState, Icon, Spinner } from '../../components/common';
import { patientsApi, requestsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { AttachmentGrid } from './attachments/Attachments';
import { AttachmentUploader } from './attachments/AttachmentUploader';
import { forgetAttachment, invalidatePatientAttachments } from './attachments/upload';
import { usePoll } from '../../hooks/usePolling';
import { ocrInFlight } from '../../domain/ocrText';

export function AttachmentsPanel({ patientId, canUpload = true, lockedCategory = null, defaultRequestId = null }) {
  const { success, error: toastError } = useNotifications();
  const [rows, setRows] = useState(null);
  const [categories, setCategories] = useState([]);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [list, cats, reqs] = await Promise.all([
        patientsApi.attachments(patientId),
        patientsApi.categories().catch(() => []),
        canUpload ? requestsApi.list({ hospitalPatientId: patientId, open: '1' }).catch(() => []) : [],
      ]);
      setRows(list);
      setCategories(cats || []);
      setRequests(Array.isArray(reqs) ? reqs : []);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Pictures are read on the server right after they are filed; while any is
  // still being read, the list refreshes itself so the text appears unasked.
  const reading = ocrInFlight(rows);
  usePoll(
    async () => {
      const list = await patientsApi.attachments(patientId);
      setRows(list);
      invalidatePatientAttachments(patientId);
    },
    reading ? 4000 : null,
    { immediate: false },
  );

  const counts = useMemo(
    () => (rows || []).reduce((map, row) => ({ ...map, [row.category]: (map[row.category] || 0) + 1 }), {}),
    [rows],
  );
  const shown = filter === 'all' ? rows || [] : (rows || []).filter((r) => r.category === filter);

  const remove = async () => {
    const row = removing;
    setRemoving(null);
    try {
      await patientsApi.removeAttachment(row.id);
      forgetAttachment(row.id);
      invalidatePatientAttachments(patientId);
      setRows((list) => list.filter((r) => r.id !== row.id));
      success('تم حذف المرفق');
    } catch (err) {
      toastError(err.message);
    }
  };

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!rows) return <Spinner label="جارٍ تحميل المرفقات" variant="inline" />;

  return (
    <div className="space-y-5">
      {canUpload && (
        <AttachmentUploader
          patientId={patientId}
          categories={categories}
          lockedCategory={lockedCategory}
          requests={requests}
          defaultRequestId={defaultRequestId}
          onUploaded={(row) => setRows((list) => [row, ...(list || []).filter((r) => r.id !== row.id)])}
        />
      )}

      {Object.keys(counts).length > 1 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="تصفية المرفقات">
          <button type="button" aria-pressed={filter === 'all'} onClick={() => setFilter('all')} className={`chip-toggle ${filter === 'all' ? 'chip-toggle--on' : ''}`}>
            الكل <span className="chip-toggle__count">{rows.length}</span>
          </button>
          {Object.entries(counts).map(([cat, n]) => (
            <button key={cat} type="button" aria-pressed={filter === cat} onClick={() => setFilter(cat)} className={`chip-toggle ${filter === cat ? 'chip-toggle--on' : ''}`}>
              {cat} <span className="chip-toggle__count">{n}</span>
            </button>
          ))}
        </div>
      )}

      {reading && (
        <p className="ocr-reading-strip flex items-center gap-2 rounded-xl px-3 py-2 text-xs">
          <Icon name="scan-text" size={14} /> تُقرأ النصوص والجداول من الصور المرفوعة الآن على خادم المستشفى — تظهر تلقائياً عند الانتهاء.
        </p>
      )}

      <AttachmentGrid
        rows={shown}
        linkedRequestId={defaultRequestId}
        onRemove={canUpload ? setRemoving : undefined}
        onRowChange={(updated) => updated && setRows((list) => (list || []).map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        title="حذف المرفق"
        message={removing ? `سيُحذف "${removing.title || removing.fileName}" من ملف المريض لكل الأقسام.` : ''}
        confirmLabel="حذف"
        destructive
      />
    </div>
  );
}

export default AttachmentsPanel;
